import React, { useState } from 'react';
import { PersonFinal } from '@/api/entities';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Search, Edit2, Trash2, Save, X, Loader2, UserX } from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function PersonaSearchManager({ currentUser }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [editingPersona, setEditingPersona] = useState(null);
  const [editedTitle, setEditedTitle] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      toast.error('Please enter a search term');
      return;
    }

    setIsSearching(true);
    try {
      // Fetch all personas and filter client-side for more flexible matching
      const allPersonas = await PersonFinal.list();
      
      const searchLower = searchTerm.toLowerCase();
      const filtered = allPersonas.filter(persona => {
        const firstName = (persona.first_name || '').toLowerCase();
        const lastName = (persona.last_name || '').toLowerCase();
        const fullName = (persona.full_name || '').toLowerCase();
        const roleTitle = (persona.role_title || '').toLowerCase();
        const accountName = (persona.account_name || '').toLowerCase();
        const email = (persona.email || '').toLowerCase();
        
        return firstName.includes(searchLower) ||
               lastName.includes(searchLower) ||
               fullName.includes(searchLower) ||
               roleTitle.includes(searchLower) ||
               accountName.includes(searchLower) ||
               email.includes(searchLower);
      });

      setSearchResults(filtered);
      toast.success(`Found ${filtered.length} persona${filtered.length !== 1 ? 's' : ''}`);
    } catch (error) {
      console.error('Search error:', error);
      toast.error('Failed to search personas');
    } finally {
      setIsSearching(false);
    }
  };

  const handleStartEdit = (persona) => {
    setEditingPersona(persona);
    setEditedTitle(persona.role_title || '');
  };

  const handleCancelEdit = () => {
    setEditingPersona(null);
    setEditedTitle('');
  };

  const handleSaveEdit = async () => {
    if (!editingPersona) return;

    setIsSaving(true);
    try {
      await PersonFinal.update(editingPersona.id, {
        role_title: editedTitle.trim()
      });

      // Update local state
      setSearchResults(prev => prev.map(p => 
        p.id === editingPersona.id 
          ? { ...p, role_title: editedTitle.trim() }
          : p
      ));

      toast.success('Job title updated successfully');
      setEditingPersona(null);
      setEditedTitle('');
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to update job title');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteClick = (personaId) => {
    setDeleteConfirmId(personaId);
  };

  const handleConfirmDelete = async () => {
    if (!deleteConfirmId) return;

    setIsDeleting(true);
    try {
      await PersonFinal.delete(deleteConfirmId);

      // Remove from local state
      setSearchResults(prev => prev.filter(p => p.id !== deleteConfirmId));

      toast.success('Persona deleted successfully');
      setDeleteConfirmId(null);
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete persona');
    } finally {
      setIsDeleting(false);
    }
  };

  // Only super admins can use this
  if (!currentUser || currentUser.role !== 'admin') {
    return (
      <Card className="border-red-200 bg-red-50">
        <CardContent className="pt-6">
          <p className="text-red-700 text-center">
            This feature is only available to super administrators.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="w-5 h-5" />
            Persona Search & Management
          </CardTitle>
          <CardDescription>
            Search personas by name, job title, account, or email. Edit their job titles or delete personas.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            <Input
              placeholder="Search by name, job title, account, or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSearch();
                }
              }}
              className="flex-1"
            />
            <Button 
              onClick={handleSearch} 
              disabled={isSearching}
              className="bg-[#00C2C2] hover:bg-[#00A3A3]"
            >
              {isSearching ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Searching...
                </>
              ) : (
                <>
                  <Search className="w-4 h-4 mr-2" />
                  Search
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Search Results */}
      {searchResults.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>
              Search Results ({searchResults.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {searchResults.map((persona) => {
                const isEditing = editingPersona?.id === persona.id;
                const fullName = persona.full_name || `${persona.first_name} ${persona.last_name}`;

                return (
                  <div
                    key={persona.id}
                    className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        {/* Avatar */}
                        <div className="flex-shrink-0">
                          {persona.profile_photo_url ? (
                            <img
                              src={persona.profile_photo_url}
                              alt={fullName}
                              className="w-12 h-12 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 font-semibold">
                              {persona.first_name?.[0]}{persona.last_name?.[0]}
                            </div>
                          )}
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-semibold text-gray-900">{fullName}</h4>
                            {persona.tenant_id && (
                              <Badge variant="outline" className="text-xs">
                                {persona.tenant_id}
                              </Badge>
                            )}
                          </div>

                          {isEditing ? (
                            <div className="flex items-center gap-2 mt-2">
                              <Input
                                value={editedTitle}
                                onChange={(e) => setEditedTitle(e.target.value)}
                                placeholder="Enter job title..."
                                className="flex-1"
                                autoFocus
                              />
                              <Button
                                size="sm"
                                onClick={handleSaveEdit}
                                disabled={isSaving}
                                className="bg-green-600 hover:bg-green-700"
                              >
                                {isSaving ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Save className="w-4 h-4" />
                                )}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={handleCancelEdit}
                                disabled={isSaving}
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          ) : (
                            <>
                              <p className="text-sm text-gray-600 break-words">
                                {persona.role_title || <span className="italic text-gray-400">No job title</span>}
                              </p>
                              <p className="text-sm text-gray-500 mt-1">{persona.account_name}</p>
                              {persona.email && (
                                <p className="text-xs text-gray-400 mt-1">{persona.email}</p>
                              )}
                              {persona.persona_fit_score && (
                                <Badge variant="outline" className="mt-2">
                                  Fit Score: {persona.persona_fit_score}
                                </Badge>
                              )}
                            </>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      {!isEditing && (
                        <div className="flex gap-2 flex-shrink-0">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleStartEdit(persona)}
                            title="Edit job title"
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeleteClick(persona.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            title="Delete persona"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Empty State */}
      {searchTerm && !isSearching && searchResults.length === 0 && (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-8">
              <UserX className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600">No personas found matching "{searchTerm}"</p>
              <p className="text-sm text-gray-500 mt-1">Try a different search term</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteConfirmId !== null} onOpenChange={(open) => !open && setDeleteConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Persona?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this persona from the database. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete Persona'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}